/**
 * @fileoverview Taggable data sources for report templates (MOR-962).
 *
 * The template editor lets practitioners @-mention a data source inside a
 * section's wording (a `<span data-morgan-data-tag="…">` chip). This is the
 * catalogue those mentions draw from: the summarization data agents, the
 * voidable-transaction heads, the offence heads, Books & Records, and
 * job-level case data (Proofs of Debt, solvency settings, the overview
 * background summary).
 *
 * Dependency-light on purpose: imported by the React template editor (via the
 * `@cvx/*` alias, like `uiMetadata.ts`) AND by the report-generation action
 * (to resolve tag ids back to display names for the writer prompts).
 */

import { ALL_MANIFEST_UI_METADATA } from "../summarization/agents/uiMetadata";
import {
  FLAGGED_HEAD_CONFIGS,
  FLAGGED_HEAD_ORDER,
} from "../summarization/agents/flaggedTransactions/headConfigs";
import {
  OFFENCE_HEAD_CONFIGS,
  OFFENCE_HEAD_ORDER,
} from "../summarization/agents/civilCriminalOffences/headConfigs";

export interface ReportDataSource {
  /** The manifest / summarizationTasks agentId, e.g. "sum_bank_transactions". */
  id: string;
  /** Display name, e.g. "Bank Transactions". */
  label: string;
  /** Dropdown grouping. */
  group:
    | "Data agent"
    | "Voidable transactions"
    | "Offences"
    | "Review"
    | "Case data";
  /**
   * Where the writer finds this source when it isn't a `summaries[]` agent —
   * folded into the TAGGED DATA SOURCES prompt line.
   */
  contextHint?: string;
}

/** Job-overview background summary — stored on `jobs.overviewSummaryHtml`. */
export const JOB_BACKGROUND_SOURCE_ID = "job_background";

export const REPORT_DATA_SOURCES: ReadonlyArray<ReportDataSource> = [
  ...ALL_MANIFEST_UI_METADATA.map((m) => ({
    id: m.id,
    label: m.name,
    group: "Data agent" as const,
  })),
  {
    id: "sum_asset_recovery",
    label: "Asset Recovery & Outcomes",
    group: "Data agent" as const,
  },
  {
    id: "sum_books_and_records",
    label: "Books & Records",
    group: "Review" as const,
  },
  {
    id: JOB_BACKGROUND_SOURCE_ID,
    label: "Background",
    group: "Case data" as const,
    contextHint:
      "the backgroundSummary field in the analysed case data (the job overview background of the enterprise — already drafted and practitioner-editable)",
  },
  {
    id: "proofs_of_debt",
    label: "Proofs of Debt",
    group: "Case data" as const,
    contextHint:
      "the proofsOfDebt array in the analysed case data (the POD register)",
  },
  {
    id: "solvency_analysis",
    label: "Solvency Analysis",
    group: "Case data" as const,
    contextHint:
      "the solvencyAnalysis field plus the Financial Analysis summary in the analysed case data",
  },
  ...FLAGGED_HEAD_ORDER.map((key) => ({
    id: FLAGGED_HEAD_CONFIGS[key].manifestId,
    label: FLAGGED_HEAD_CONFIGS[key].name,
    group: "Voidable transactions" as const,
  })),
  ...OFFENCE_HEAD_ORDER.map((key) => ({
    id: OFFENCE_HEAD_CONFIGS[key].manifestId,
    label: OFFENCE_HEAD_CONFIGS[key].name,
    group: "Offences" as const,
  })),
];

export const REPORT_DATA_SOURCE_BY_ID: ReadonlyMap<string, ReportDataSource> =
  new Map(REPORT_DATA_SOURCES.map((s) => [s.id, s]));

/** Tag ids present in a wording HTML string (data-morgan-data-tag spans). */
export function extractDataTagIds(html: string): string[] {
  const ids = new Set<string>();
  for (const m of html.matchAll(/data-morgan-data-tag="([^"]+)"/g)) {
    ids.add(m[1]);
  }
  return Array.from(ids);
}

const BACKGROUND_MISSING = "<p>[to be confirmed]</p>";

/**
 * Replace an @Background chip with the job overview HTML before the writer
 * model sees the wording. The summary is already practitioner-editable
 * report-ready prose, so it is inserted in code (like a merge field) rather
 * than paraphrased. A chip sitting alone in a `<p>` is lifted out so the
 * summary's own paragraphs are not nested.
 */
export function spliceBackgroundDataTag(
  html: string,
  backgroundHtml: string | null | undefined,
): string {
  if (!html.includes(`data-morgan-data-tag="${JOB_BACKGROUND_SOURCE_ID}"`)) {
    return html;
  }
  const trimmed = backgroundHtml?.trim() ?? "";
  const block = trimmed.length > 0 ? trimmed : BACKGROUND_MISSING;
  const wrapped = new RegExp(
    `<p[^>]*>\\s*<span[^>]*data-morgan-data-tag="${JOB_BACKGROUND_SOURCE_ID}"[^>]*>[\\s\\S]*?<\\/span>\\s*<\\/p>`,
    "gi",
  );
  const span = new RegExp(
    `<span[^>]*data-morgan-data-tag="${JOB_BACKGROUND_SOURCE_ID}"[^>]*>[\\s\\S]*?<\\/span>`,
    "gi",
  );
  return html.replace(wrapped, block).replace(span, block);
}
